import { useState, useMemo, useCallback } from 'react';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import smartleadService, {
  SmartleadConnectionStatus,
  SmartleadOverview,
  SmartleadCampaign,
} from '@/services/smartlead';
import CEOxColdEmailComponent, { DateFilter, DatePreset } from './CEOxColdEmail.component';

function getYearRange(year: number): { startDate: string; endDate: string } {
  return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
}

function getMonthRange(offset: number): { startDate: string; endDate: string } {
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const end = new Date(target.getFullYear(), target.getMonth() + 1, 0);
  return { startDate: target.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
}

function getQuarterRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), q * 3, 1);
  const end = new Date(now.getFullYear(), q * 3 + 3, 0);
  return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
}

function computeOverview(campaigns: SmartleadCampaign[]): SmartleadOverview {
  let totalSent = 0;
  let totalOpened = 0;
  let totalReplied = 0;
  let totalBounced = 0;
  let totalClicked = 0;

  for (const c of campaigns) {
    totalSent += c.sent;
    totalOpened += c.opened;
    totalReplied += c.replied;
    totalBounced += c.bounced;
    totalClicked += c.clicked;
  }

  const activeCampaigns = campaigns.filter(
    (c) => c.status === 'ACTIVE' || c.status === 'START'
  ).length;

  return {
    totalCampaigns: campaigns.length,
    activeCampaigns,
    totalSent,
    totalOpened,
    totalReplied,
    totalBounced,
    totalClicked,
    avgOpenRate: totalSent > 0 ? Math.round((totalOpened / totalSent) * 100) : 0,
    avgReplyRate: totalSent > 0 ? Math.round((totalReplied / totalSent) * 100) : 0,
    avgBounceRate: totalSent > 0 ? Math.round((totalBounced / totalSent) * 100) : 0,
    campaigns,
    lastSyncedAt: null,
  };
}

const DEFAULT_OVERVIEW: SmartleadOverview = {
  totalCampaigns: 0,
  activeCampaigns: 0,
  totalSent: 0,
  totalOpened: 0,
  totalReplied: 0,
  totalBounced: 0,
  totalClicked: 0,
  avgOpenRate: 0,
  avgReplyRate: 0,
  avgBounceRate: 0,
  campaigns: [],
  lastSyncedAt: null,
};

const CEOxColdEmailContainer = function (): JSX.Element {
  const orgId = getActiveOrgId();
  const now = new Date();
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    preset: 'this_year',
    ...getYearRange(now.getFullYear()),
  });

  const handlePresetChange = useCallback((preset: DatePreset) => {
    const year = new Date().getFullYear();
    switch (preset) {
      case 'this_month': setDateFilter({ preset, ...getMonthRange(0) }); break;
      case 'last_month': setDateFilter({ preset, ...getMonthRange(-1) }); break;
      case 'this_quarter': setDateFilter({ preset, ...getQuarterRange() }); break;
      case 'this_year': setDateFilter({ preset, ...getYearRange(year) }); break;
      case 'last_year': setDateFilter({ preset, ...getYearRange(year - 1) }); break;
      default: setDateFilter((prev) => ({ ...prev, preset }));
    }
  }, []);

  const handleCustomDateChange = useCallback((startDate: string, endDate: string) => {
    setDateFilter({ preset: 'custom', startDate, endDate });
  }, []);

  const { data: status } = useSeoFetch<SmartleadConnectionStatus>(
    ['ceox-smartlead-status', orgId],
    () => smartleadService.getConnectionStatus(),
    { retry: false }
  );

  const isConnected = status?.isConnected ?? false;

  const { data: overview, isLoading, refetch } = useSeoFetch<SmartleadOverview>(
    ['ceox-smartlead-overview', orgId],
    () => smartleadService.getOverview(),
    { retry: false, enabled: isConnected }
  );

  const [isSyncing, setIsSyncing] = useState(false);

  const handleSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      await smartleadService.sync();
      refetch();
    } finally {
      setIsSyncing(false);
    }
  }, [refetch]);

  const filteredOverview = useMemo(() => {
    if (!overview) return DEFAULT_OVERVIEW;
    const { startDate, endDate } = dateFilter;
    const filtered = overview.campaigns.filter((c) => {
      if (!c.createdAt) return true;
      const d = c.createdAt.split('T')[0];
      return d >= startDate && d <= endDate;
    });
    const result = computeOverview(filtered);
    result.lastSyncedAt = overview.lastSyncedAt;
    return result;
  }, [overview, dateFilter]);

  return (
    <CEOxColdEmailComponent
      isConnected={isConnected}
      overview={filteredOverview}
      isLoading={isLoading}
      isSyncing={isSyncing}
      onSync={handleSync}
      dateFilter={dateFilter}
      onPresetChange={handlePresetChange}
      onCustomDateChange={handleCustomDateChange}
    />
  );
};

export default CEOxColdEmailContainer;
